import React, {useEffect, useState, Fragment} from 'react'
import {Collapse, NavItem, NavLink, Badge} from 'reactstrap'
import classnames from 'classnames'
import {getCatalogCategories} from '../../services/ProductLinesServices'
import PropTypes from 'prop-types'
import InfoCircle from '../../assets/icons/info-circle.svg'
import { FormGroup, Label, Input, UncontrolledTooltip } from "reactstrap";
import ConfirmModal from "../../shared/ConfirmModal";
import {hasFeatureTag} from '../../helpers/settings'
import useGTM, { buildCategoryPayload, getGTMAttributes } from '../../hooks/useGTM'

const MockupGeneratorNavTab = (props) => {
    const {selectedCategory, handleSelectCategory, filterShopifyProducts, handleFilterShopifyProducts, confirmModal, setConfirmModal, setQuery} = props
    const [categories, setCategories] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                const {data, success, message} = await getCatalogCategories()
                if (!success) throw new Error(message)
                setCategories(data)
            } catch (e) {
                setError(e.message)
            } finally {
                setLoading(false)
            }
        }
        fetchCategory()
    }, [])

    return (
        <div className="ProductCatalogNav nav-tabs mt-1 col-2">
            {loading ? (
                ''
            ) : error ? (
                <p className="text-danger fs-14 pl-3 pr-3">
                    <i>{error}</i>
                </p>
            ) : (
                <Fragment>
                    {hasFeatureTag('shopifiy_public_app') && (
                        <div className="CategoryItemFilter">
                            <FormGroup switch>
                                <Input
                                    type="switch"
                                    id={'autoPaymentSwitch'}
                                    checked={filterShopifyProducts}
                                    onClick={(e) => handleFilterShopifyProducts(e.target.checked)}
                                />
                                <Label check>Show only Shopify products</Label>
                            </FormGroup>
                            <img
                                aria-hidden="true"
                                id="shopify-products-tooltip"
                                src={InfoCircle}
                                alt="info-icon"
                            ></img>
                            <UncontrolledTooltip
                                placement="top"
                                target="shopify-products-tooltip"
                                className="shopify-tooltip"
                            >
                                Show only Shopify-supported products. Turn off to include unsupported ones.
                            </UncontrolledTooltip>
                            <ConfirmModal
                                isOpen={confirmModal}
                                onCancel={() => setConfirmModal(false)}
                                onOk={() => {
                                    setQuery((prev) => ({...prev, page: 1, onlyShopifyProducts: false}))
                                    setConfirmModal(false)
                                }}
                                title="Are you sure you want to continue?"
                                message={<div>You are choosing to show products that are not supported for the Shopify market you are selling on.<br/>
                                    These products cannot be synced to your Shopify store after you finish the mockup.</div>}
                                customOkButton={<button className="btn btn-primary ml-3">Yes</button>}
                                customCancelButton={<button className="btn bg-light border">No</button>}
                            />
                        </div>
                    )}
                    <div className="CategoryItem">
                        <NavItem>
                            <NavLink
                                className={classnames('cursor-pointer font-weight-500', {active: selectedCategory === ''})}
                                onClick={() => {
                                    const category = buildCategoryPayload(null)
                                    handleSelectCategory(category)
                                }}
                                {...getGTMAttributes('select_category', { category: buildCategoryPayload(null) })}
                            >
                                All Products
                            </NavLink>
                        </NavItem>
                    </div>
                    {Array.isArray(categories) &&
                        categories.map((category, index) => {
                            return (
                                <RecursiveItem
                                    index={index}
                                    category={category}
                                    categoryChildren={category.children}
                                    selectedCategory={selectedCategory}
                                    handleSelectCategory={handleSelectCategory}
                                />
                            )
                        })}
                </Fragment>
            )}
        </div>
    )
}

const CategoryItem = ({selectedCategory, category, isOpen, categoryChildren, handleSelectCategory, index}) => {
    const isParent = Array.isArray(categoryChildren) && categoryChildren.some((child) => child._id === selectedCategory)

    const active = selectedCategory === category._id

    return (
        <NavItem className="w-100" key={`SidebarListProductMockup_${index}`}>
            <NavLink
                className={classnames('cursor-pointer d-flex align-items-center', {
                    active: active || isParent,
                    'font-weight-500': category.level === 0,
                })}
                onClick={() => {
                    if (!isOpen) {
                        const categoryPayload = buildCategoryPayload(category)
                        handleSelectCategory(categoryPayload)
                    }
                }}
                {...getGTMAttributes('select_category', { category: buildCategoryPayload(category) })}
            >
                <span>{category.name}</span>
                {category.slug === 'preview-3d-model' && (
                    <Badge color="danger" className="ml-2">
                        New
                    </Badge>
                )}
            </NavLink>
        </NavItem>
    )
}

const RecursiveItem = ({selectedCategory, category, categoryChildren, handleSelectCategory, index}) => {
    const [isOpen, setIsOpen] = useState(false)

    const toggleCollapse = () => {
        setIsOpen(!isOpen)
    }

    if (!category || (category && category.level > 1)) return null

    if (!category.children) {
        return (
            <div className="CategoryItem">
                <CategoryItem
                    index={index}
                    isOpen={isOpen}
                    category={category}
                    selectedCategory={selectedCategory}
                    handleSelectCategory={handleSelectCategory}
                />
            </div>
        )
    }

    return (
        <Fragment>
            <div
                className={classnames('CategoryItem d-flex align-items-center justify-content-between pr-3', {
                    IsOpen: isOpen,
                })}
                onClick={toggleCollapse}
            >
                <CategoryItem
                    index={index}
                    category={category}
                    categoryChildren={categoryChildren}
                    selectedCategory={selectedCategory}
                    handleSelectCategory={handleSelectCategory}
                />
                {!!category.children && category.level < 1 && (
                    <div className="BtnArrow">
                        <i className={`fa ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'} cursor-pointer`} />
                    </div>
                )}
            </div>
            <Collapse isOpen={isOpen} className='pl-4'>
                {category.children &&
                    category.children.map((item, index) => (
                        <RecursiveItem
                            index={index}
                            selectedCategory={selectedCategory}
                            category={item}
                            categoryChildren={categoryChildren}
                            level={category + 1}
                            handleSelectCategory={handleSelectCategory}
                        />
                    ))}
            </Collapse>
        </Fragment>
    )
}

MockupGeneratorNavTab.propTypes = {
    activeTab: PropTypes.string,
    onHandleTabs: PropTypes.func,
}

export default MockupGeneratorNavTab

